/**
 * Content Image Assets
 *
 * Stock/background images for various UI contexts.
 * Portugal-themed imagery for the mobility platform.
 */

import type { AssetPath } from '../types';

/**
 * Base path for content images
 */
const CONTENT_BASE = '/assets/images/content';

/**
 * Content image categories
 */
export type ContentImageCategory = 'portugal' | 'lifestyle' | 'professional';

/**
 * Content image assets
 */
export const CONTENT_IMAGES = {
  // Portugal-themed images
  azulejosTiles: {
    path: `${CONTENT_BASE}/azulejos-tiles.jpg`,
    alt: 'Traditional Portuguese azulejos tiles',
    width: 1200,
    height: 800,
  },
  azulejosBuilding: {
    path: `${CONTENT_BASE}/azulejos-building.jpg`,
    alt: 'Building with Portuguese azulejos',
    width: 1200,
    height: 800,
  },
  lisbonCastle: {
    path: `${CONTENT_BASE}/lisbon-castle.jpg`,
    alt: 'Lisbon Castle (São Jorge)',
    width: 1200,
    height: 800,
  },
  lisbonSunset: {
    path: `${CONTENT_BASE}/lisbon-sunset.jpg`,
    alt: 'Lisbon sunset view',
    width: 1200,
    height: 800,
  },
  lisbonWaterfront: {
    path: `${CONTENT_BASE}/lisbon-waterfront.jpg`,
    alt: 'Lisbon waterfront',
    width: 1200,
    height: 800,
  },
  pastelDeNata: {
    path: `${CONTENT_BASE}/pastel-de-nata.jpg`,
    alt: 'Traditional Portuguese pastéis de nata',
    width: 1200,
    height: 800,
  },

  // Lifestyle images
  meditation: {
    path: `${CONTENT_BASE}/meditation.jpg`,
    alt: 'Person meditating',
    width: 1200,
    height: 800,
  },

  // Professional images
  professionalCafe: {
    path: `${CONTENT_BASE}/professional-cafe.jpg`,
    alt: 'Professional working in café',
    width: 1200,
    height: 800,
  },
  womanWorking: {
    path: `${CONTENT_BASE}/woman-working.jpg`,
    alt: 'Woman working on laptop',
    width: 1200,
    height: 800,
  },
} as const;

/**
 * Content image type
 */
export type ContentImageKey = keyof typeof CONTENT_IMAGES;

/**
 * Get content image path
 */
export function getContentImage(key: ContentImageKey): string {
  return CONTENT_IMAGES[key].path;
}

/**
 * Get content image asset with metadata
 */
export function getContentImageAsset(key: ContentImageKey): AssetPath {
  return CONTENT_IMAGES[key];
}

/**
 * Get all content images
 */
export function getAllContentImages(): AssetPath[] {
  return Object.values(CONTENT_IMAGES);
}
